import React, { useState, useEffect } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import modularisLogo from '@/assets/logo-modularis.svg';

export default function ResetPassword() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [hasValidTokens, setHasValidTokens] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isRecoveryMode, setIsRecoveryMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const sp = new URLSearchParams(window.location.search);
      const hash = window.location.hash;
      const force = window.location.pathname === '/reset-password' || sp.get('type') === 'recovery' || hash.includes('type=recovery') || localStorage.getItem('password_recovery_mode') === 'true';
      if (force) {
        try { localStorage.setItem('password_recovery_mode', 'true'); } catch {}
        return true;
      }
    }
    return false;
  });

  // Fallback: si estamos en /reset-password, forzar modo recovery aunque no vengan tokens
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.pathname === '/reset-password') {
      try { localStorage.setItem('password_recovery_mode', 'true'); } catch {}
      setIsRecoveryMode(true);
    }
  }, []);

  // Check if we have the necessary tokens from URL params
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');
  const type = searchParams.get('type');
  const urlError = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  // Marcar que estamos en modo recovery para prevenir redirecciones automáticas
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    const hashParams = new URLSearchParams(hash);
    const finalType = type || hashParams.get('type');
    
    if (finalType === 'recovery') {
      setIsRecoveryMode(true);
      // Guardar en localStorage para prevenir redirecciones
      localStorage.setItem('password_recovery_mode', 'true');
    }
  }, [type]);

  // Also check for hash parameters (Supabase sometimes uses these)
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const hashAccessToken = params.get('access_token');
    const hashRefreshToken = params.get('refresh_token');
    const hashType = params.get('type');
    const hashError = params.get('error');

    console.log('URL search params:', { accessToken, refreshToken, type, urlError, errorDescription });
    console.log('URL hash params:', { hashAccessToken, hashRefreshToken, hashType, hashError });
    console.log('Full URL:', window.location.href);
  }, [accessToken, refreshToken, type, urlError, errorDescription]);

  useEffect(() => {
    const validateAndSetSession = async () => {
      setIsValidating(true);
      
      // Check if there's an error from Supabase
      if (urlError) {
        console.log('Supabase returned error:', { urlError, errorDescription });
        setValidationError(`Error de Supabase: ${errorDescription || urlError}`);
        setHasValidTokens(false);
        setIsValidating(false);
        localStorage.removeItem('password_recovery_mode');
        return;
      }

      // Check for tokens in hash (Supabase usually puts them here)
      const hash = window.location.hash.substring(1);
      const hashParams = new URLSearchParams(hash);
      const finalAccessToken = accessToken || hashParams.get('access_token');
      const finalType = type || hashParams.get('type');

      // Detect recovery mode broadly (hash, search, path or persisted flag)
      const recoveryFlag = localStorage.getItem('password_recovery_mode') === 'true';
      const isRecovery = (finalType === 'recovery') || recoveryFlag || window.location.pathname === '/reset-password';

      if (isRecovery) {
        try { localStorage.setItem('password_recovery_mode', 'true'); } catch {}
        setIsRecoveryMode(true);

        if (finalAccessToken) {
          console.log('✅ Recovery access_token detectado. Mostrando formulario sin forzar setSession.');
        } else {
          console.log('ℹ️ Modo recovery sin access_token visible; confiando en sesión temporal del SDK.');
        }

        // No forzamos setSession: Supabase maneja el token de recuperación internamente
        setHasValidTokens(true);
        setValidationError(null);
      } else {
        console.log('⚠️ Missing required parameters:', {
          hasAccessToken: !!finalAccessToken,
          type: finalType,
          searchParams: Object.fromEntries(searchParams.entries()),
          hash: window.location.hash
        });
        setValidationError('Faltan parámetros requeridos en el enlace. El enlace puede haber expirado o ser inválido.');
        setHasValidTokens(false);
        localStorage.removeItem('password_recovery_mode');
      }
      
      setIsValidating(false);
    };

    validateAndSetSession();
  }, [accessToken, refreshToken, type, urlError, errorDescription, searchParams]);

  // CRÍTICO: Prevenir acceso al app hasta que se cambie la contraseña
  // Si estamos en modo recovery, SIEMPRE mostrar el formulario hasta que se cambie la contraseña
  if (isRecoveryMode && user && !passwordChanged) {
    console.log('🔒 Usuario en modo recovery - mostrando formulario de cambio de contraseña');
    // No hacer nada, dejar que se renderice el formulario
  } else if (!isValidating && user && !isRecoveryMode && !passwordChanged) {
    // Usuario ya autenticado pero no en modo recovery - redirigir a auth
    console.log('Usuario autenticado normalmente - redirigiendo a auth');
    return <Navigate to="/auth" replace />;
  }

  // Redirect to app only after password has been successfully changed
  if (passwordChanged && user) {
    console.log('✅ Contraseña cambiada exitosamente - limpiando modo recovery y redirigiendo');
    localStorage.removeItem('password_recovery_mode');
    return <Navigate to="/" replace />;
  }

  // Show loading while validating tokens
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show error if no valid recovery tokens provided
  if (!hasValidTokens) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-3 sm:p-4 md:p-6">
        <div className="w-full max-w-sm sm:max-w-md">
          <Card className="shadow-xl border-0 bg-card/95 backdrop-blur-sm rounded-2xl">
            <CardHeader className="space-y-3 pb-6">
              <h2 className="font-semibold tracking-tight text-xl">Enlace Inválido</h2>
              <CardDescription className="text-sm text-muted-foreground">
                {validationError || 'El enlace de restablecimiento no es válido, ha expirado o ya fue usado. Por favor, solicita un nuevo enlace de restablecimiento.'}
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex flex-col space-y-3">
              <Button 
                onClick={() => window.location.href = '/auth'}
                className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary/90"
              >
                Solicitar Nuevo Enlace
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Ve a "¿Olvidaste tu contraseña?" para recibir un nuevo enlace
              </p>
              {validationError && (
                <div className="text-xs text-destructive text-center bg-destructive/10 p-2 rounded">
                  Error técnico: {validationError}
                </div>
              )}
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  const generateSecurePassword = () => {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let newPassword = '';
    
    // Asegurar que tenga al menos: 1 mayúscula, 1 minúscula, 1 número, 1 símbolo
    newPassword += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    newPassword += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    newPassword += '0123456789'[Math.floor(Math.random() * 10)];
    newPassword += '!@#$%^&*'[Math.floor(Math.random() * 8)];
    
    // Completar el resto de caracteres
    for (let i = newPassword.length; i < length; i++) {
      newPassword += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Mezclar los caracteres
    newPassword = newPassword.split('').sort(() => Math.random() - 0.5).join('');
    
    setPassword(newPassword);
    setConfirmPassword(newPassword);
    toast.success('Contraseña segura generada');
  };

  const handlePasswordUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setIsLoading(false);
      return;
    }

    try {
      console.log('🔄 Actualizando contraseña...');
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        console.error('❌ Error al actualizar contraseña:', updateError);
        setError(updateError.message);
      } else {
        console.log('✅ Contraseña actualizada exitosamente');
        setSuccess('Contraseña actualizada exitosamente');
        setPasswordChanged(true); // CRÍTICO: Marcar como cambiada
        toast.success('Contraseña actualizada exitosamente');
        
        // Limpiar el modo recovery del localStorage
        localStorage.removeItem('password_recovery_mode');
        
        // Pequeña demora antes de redirigir
        setTimeout(() => {
          console.log('✅ Redirigiendo al dashboard...');
        }, 1500);
      }
    } catch (err: any) {
      console.error('❌ Error al actualizar contraseña:', err);
      setError(err.message || 'Error al actualizar la contraseña');
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-3 sm:p-4 md:p-6">
      <div className="w-full max-w-sm sm:max-w-md">
        <div className="flex flex-col items-center justify-center mb-6 sm:mb-8 auth-header">
          <div className="relative">
            <img 
              src={modularisLogo}
              alt="Modularis Logo" 
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="h-16 sm:h-20 md:h-24 w-auto object-contain mb-3"
            />
          </div>
          <p className="text-sm text-muted-foreground text-center">El ERP que piensa contigo</p>
        </div>

        <Card className="shadow-xl border-0 bg-card/95 backdrop-blur-sm rounded-2xl">
          <CardHeader className="space-y-3 pb-6">
            <h2 className="font-semibold tracking-tight text-xl">Nueva Contraseña</h2>
            <CardDescription className="text-sm text-muted-foreground">
              Ingresa tu nueva contraseña para completar el restablecimiento
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handlePasswordUpdate}>
            <CardContent className="space-y-5">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {success && (
                <Alert>
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="new-password" className="text-sm font-medium">
                    Nueva Contraseña
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={generateSecurePassword}
                    disabled={isLoading}
                    className="h-8 text-xs gap-1.5"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Generar segura
                  </Button>
                </div>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  disabled={isLoading}
                  className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-sm font-medium">
                  Confirmar Contraseña
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirma tu nueva contraseña"
                  required
                  disabled={isLoading}
                  className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                />
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4 pt-2">
              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary/90 active:scale-[0.98] shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50" 
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                Actualizar Contraseña
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}